import { useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from '@cravo/i18n'
import { Sidebar } from '@/components/Sidebar'
import { Header } from '@/components/Header'
import { TopNav } from '@/components/TopNav'
import { AiBubble } from '@/components/AiBubble'
import { OfflineBanner } from '@/components/OfflineBanner'
import { OrderAlertSettings } from '@/components/OrderAlertSettings'
import { useNewOrderAlerts } from '@/hooks/useNewOrderAlerts'
import { useDashboardPoller } from '@/hooks/useDashboardPoller'
import { partnerAppName, aiFeatureName } from '@/lib/tenant'
import { useNavStyle } from '@/lib/use-nav-style'

const TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/requests': 'Requests',
  '/orders': 'Orders',
  '/menu': 'Menu',
  '/ai': 'Cravo AI',
  '/analytics': 'Top selling',
  '/feedback': 'Customer feedback',
  '/wallet': 'Wallet',
  '/settlements': 'Settlements',
  '/bank': 'Bank & payouts',
  '/notifications': 'Notifications',
  '/support/chat': 'Live Chat Support',
  '/settings': 'Settings',
  '/support': 'Help & support',
}

export function DashboardLayout() {
  const { t } = useTranslation()
  const location = useLocation()
  const navStyle = useNavStyle()
  const [drawerOpen, setDrawerOpen] = useState(false)

  // `app-shell` modifier classes drive grid-template-columns + show/hide
  // rules for each nav style. Class names map 1:1 with NavStyle so the
  // CSS file stays the single source of layout truth.
  const shellClass = navStyle === 'topbar'
    ? 'app-shell layout-topbar'
    : navStyle === 'sidebar-mini'
      ? 'app-shell layout-mini'
      : 'app-shell'

  const titles = useMemo<Record<string, string>>(
    () => ({
      '/': t('header.pageTitles.dashboard'),
      '/requests': t('header.pageTitles.requests'),
      '/orders': t('header.pageTitles.orders'),
      '/menu': t('header.pageTitles.menu'),
      // AI title is computed dynamically below — see `pageTitle` derivation.
      '/ai': t('header.pageTitles.ai'),
      '/analytics': t('header.pageTitles.analytics'),
      '/feedback': t('header.pageTitles.feedback'),
      '/wallet': t('header.pageTitles.wallet'),
      '/settlements': t('header.pageTitles.settlements'),
      '/bank': t('header.pageTitles.bankAndPayouts'),
      '/notifications': t('header.pageTitles.notifications'),
      '/support/chat': t('header.pageTitles.liveChatSupport'),
      '/settings': t('header.pageTitles.settings'),
      '/support': t('header.pageTitles.helpAndSupport'),
    }),
    [t],
  )

  const title = location.pathname.endsWith('/chat') && location.pathname.startsWith('/orders/')
    ? t('header.pageTitles.customerChat')
    : location.pathname.startsWith('/orders/')
      ? t('header.pageTitles.orderDetail')
      : location.pathname === '/ai'
        ? aiFeatureName()
        : titles[location.pathname] || t('header.pageTitles.dashboard')

  useEffect(() => {
    document.title = `${title} — ${partnerAppName()}`
  }, [title])

  // Close drawer when route changes
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  // Close drawer on Escape
  useEffect(() => {
    if (!drawerOpen) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [drawerOpen])

  useNewOrderAlerts()
  // Global 20s dashboard poll — keeps the sidebar badge + new-order
  // alerts live on EVERY page (Orders, Menu, …), and feeds the
  // Dashboard/Requests lists via DASHBOARD_DATA_EVENT. Without it,
  // alerts only worked while Requests or Dashboard was mounted.
  useDashboardPoller()

  return (
    <div className={shellClass}>
      <div
        className={`sidebar-backdrop ${drawerOpen ? 'open' : ''}`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
      />
      {/* Sidebar always rendered so mobile drawer + accessibility focus
          trap behave consistently regardless of nav style. Desktop
          visibility is controlled by `.app-shell.layout-topbar .sidebar`
          rules; the `mini` flag only affects the desktop persistent rail. */}
      <Sidebar
        open={drawerOpen}
        mini={navStyle === 'sidebar-mini'}
        onNavigate={() => setDrawerOpen(false)}
      />
      <div style={{ overflow: 'hidden' }}>
        {navStyle === 'topbar' && (
          <TopNav onMenuClick={() => setDrawerOpen((value) => !value)} />
        )}
        <OfflineBanner />
        <Header title={title} onMenuClick={() => setDrawerOpen((value) => !value)} />
        <div className="content">
          <Outlet />
        </div>
      </div>
      <AiBubble />
      <OrderAlertSettings />
    </div>
  )
}
